import {
    IBlogCategoryDto,
    IBlogPostDto,
    IPostList,
    ISubscriptionStatusDto,
    TBlogCard,
    TBlogPostDetails,
} from '@blog/types/blog';
import { IS_DEV, TEST_ENV_CREDENTIALS } from '@constants/common';
import he from 'he';
import { wordpressApi } from './wordpressApi';

const creds =
    typeof btoa !== 'undefined' ? btoa(TEST_ENV_CREDENTIALS) : Buffer.from(TEST_ENV_CREDENTIALS).toString('base64');

const blogApi = wordpressApi.injectEndpoints({
    endpoints: (builder) => ({
        getBlogPosts: builder.query<
            IPostList<TBlogCard>,
            { per_page?: number; page?: number; search?: string; categories?: number; orderby?: string }
        >({
            query: ({ page = 1, per_page = 6, search, categories, orderby }) => {
                return {
                    url: 'wp-json/wp/v2/posts',
                    params: { page, per_page, search, categories, orderby, _embed: true },
                    ...(IS_DEV ? { headers: { Authorization: `Basic ${creds}` } } : {}),
                };
            },
            providesTags: ['Blog Posts'],
            transformResponse: (response: IBlogPostDto[], meta) => ({
                total_pages: Number(meta?.response?.headers.get('X-WP-TotalPages')),
                data: response.map((post) => ({
                    ...post,
                    title: he.decode(post.title.rendered),
                    excerpt: post.excerpt_plain_text,
                    author: post._embedded.author.map((auth) => auth.name),
                    image: post._embedded['wp:featuredmedia']
                        ? {
                              url: post._embedded['wp:featuredmedia'][0].media_details?.sizes?.['blogpost-list']
                                  ?.source_url,
                              alt_text: post._embedded['wp:featuredmedia'][0].alt_text,
                          }
                        : undefined,
                })),
            }),
            serializeQueryArgs: ({ queryArgs }) => {
                const newArgs = { ...queryArgs };
                if (newArgs.page) {
                    // Remove the page from the cache key so the cache stays valid
                    delete newArgs.page;
                }
                return newArgs;
            },
            merge: (currentCache, newItems, { arg }) => {
                const { page } = arg;
                if (!page || page === 1) {
                    return newItems;
                }
                currentCache.data.push(...newItems.data);
                return currentCache;
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page;
            },
        }),
        getBlogPostBySlug: builder.query<TBlogPostDetails | undefined, string>({
            query: (slug) => {
                return {
                    url: 'wp-json/wp/v2/posts',
                    params: { slug, _embed: true },
                };
            },
            providesTags: ['Blog Post'],

            transformResponse: (response: IBlogPostDto[]) => {
                const post = response[0];
                if (post) {
                    return {
                        ...post,
                        author: post._embedded.author,
                        title: he.decode(post.title.rendered),
                        content: post.content.rendered,
                        related_posts: post.related_posts.map((p) => {
                            return { ...p, title: he.decode(p.title) };
                        }),
                    };
                }
            },
        }),
        getCategories: builder.query<IBlogCategoryDto[], void>({
            query: () => ({
                url: 'wp-json/wp/v2/categories',
                params: { _fields: ['id', 'name'], per_page: 100 },
            }),
            transformResponse: (response: IBlogCategoryDto[]) => {
                return response.map((cat) => ({
                    id: cat.id,
                    name: he.decode(cat.name),
                }));
            },
            providesTags: ['Blog Categories'],
        }),
        checkBlogSubscription: builder.query<ISubscriptionStatusDto, string>({
            query: (email) => {
                return {
                    url: 'wp-admin/admin-ajax.php',
                    method: 'post',
                    body: new URLSearchParams({ action: 'email_subscription_check', email }),
                    ...(IS_DEV ? { headers: { Authorization: `Basic ${creds}` } } : {}),
                };
            },
            providesTags: ['Blog Subscription'],
        }),
        subscribeToBlog: builder.mutation<void, string>({
            query: (email) => {
                return {
                    url: 'wp-admin/admin-ajax.php',
                    method: 'post',
                    body: new URLSearchParams({ action: 'email_subscription', email }),
                    ...(IS_DEV ? { headers: { Authorization: `Basic ${creds}` } } : {}),
                };
            },
            invalidatesTags: ['Blog Subscription'],
        }),
        unsubscribeToBlog: builder.mutation<void, string>({
            query: (email) => {
                return {
                    url: 'wp-admin/admin-ajax.php',
                    method: 'post',
                    body: new URLSearchParams({ action: 'email_unsubscription', email }),
                    ...(IS_DEV ? { headers: { Authorization: `Basic ${creds}` } } : {}),
                };
            },
            invalidatesTags: ['Blog Subscription'],
        }),
    }),
});

export const {
    useGetBlogPostsQuery,
    useLazyGetBlogPostsQuery,
    useGetCategoriesQuery,
    useCheckBlogSubscriptionQuery,
    useSubscribeToBlogMutation,
    useUnsubscribeToBlogMutation,
    useGetBlogPostBySlugQuery,
    useLazyGetBlogPostBySlugQuery,
    usePrefetch,
} = blogApi;
export default blogApi;
